// src/store/content.js
import { API_URL } from "@/mainconfig";

const TTL_MS = 5 * 60 * 1000;
let inFlight = null;

const state = {
    loading: false,
    error: null,
    errorMessage: null,
    byTag: {},
    lastFetchedAt: null,
};

const getters = {
    isLoading: (s) => !!s.loading,
    isLoaded: (s) => Object.keys(s.byTag || {}).length > 0,
    hasError: (s) => !!s.errorMessage,
    isReady: (s, g) => !g.isLoading && (g.isLoaded || g.hasError),
    getContentError: (s) => s.errorMessage,
    tagsList: (s) => Object.keys(s.byTag),
    itemsByTag: (s) => (tag) => s.byTag[tag?.toLowerCase?.() || tag] || [],
    hasTag: (s) => (tag) => !!s.byTag[tag?.toLowerCase?.() || tag]?.length,
    isStale: (s) => !s.lastFetchedAt || Date.now() - s.lastFetchedAt > TTL_MS,
};

const actions = {
    async fetchAll({ state, commit, getters }, options = {}) {
        const { force = false } = options;
        if (!force && getters.isLoaded && !getters.isStale) {
            return { code: 0, data: state.byTag };
        }
        if (inFlight) return inFlight;

        commit("setLoading", true);
        commit("setError", null);

        inFlight = (async () => {
            try {
                const res = await API_URL.get("/content/tag/");
                const payload = res?.data;
                if (
                    !payload ||
                    payload.code !== 0 ||
                    !Array.isArray(payload.data)
                ) {
                    throw new Error("Invalid response shape.");
                }
                const normalized = normalizeByTag(payload.data);
                commit("setByTag", normalized);
                commit("setLastFetchedAt", Date.now());
                return { code: 0, data: normalized };
            } catch (error) {
                const message =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to fetch content.";
                commit("setError", message);
                throw error;
            } finally {
                commit("setLoading", false);
                inFlight = null;
            }
        })();

        return inFlight;
    },

    async ensureTag({ getters, dispatch, state }, tag) {
        const key = tag?.toLowerCase?.() || tag;
        if (getters.hasTag(key)) return state.byTag[key];
        await dispatch("fetchAll").catch(() => {});
        return state.byTag[key] || [];
    },
};

const mutations = {
    setLoading(s, v) {
        s.loading = !!v;
    },
    setError(s, e) {
        s.error = e;
        s.errorMessage = e ? String(e) : null;
    },
    setByTag(s, byTag) {
        s.byTag = byTag || {};
    },
    setLastFetchedAt(s, ts) {
        s.lastFetchedAt = ts ?? Date.now();
    },
    clear(s) {
        s.byTag = {};
        s.lastFetchedAt = null;
        s.error = null;
        s.errorMessage = null;
        s.loading = false;
    },
};

export default { namespaced: true, state, getters, actions, mutations };

function normalizeByTag(items) {
    const byTag = {};
    for (const raw of items) {
        const key = (raw?.TagName || "unknown").toLowerCase();
        if (!byTag[key]) byTag[key] = [];
        byTag[key].push(mapItem(raw));
    }
    return byTag;
}
function mapItem(raw) {
    return {
        title: raw?.Title ?? "",
        desc: raw?.Desc ?? "",
        imageId: raw?.ImageId ?? null,
        imageUrl: raw?.ImageUrl ?? "",
        link: raw?.Link ?? "",
        other1: raw?.OtherColumn1 ?? "",
        other2: raw?.OtherColumn2 ?? "",
        other3: raw?.OtherColumn3 ?? "",
        tagName: raw?.TagName?.toLowerCase?.() ?? "",
    };
}
